import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  User as UserIcon,
  LogOut,
  ChevronDown,
  Search,
  FileText,
  Calculator,
  ClipboardList,
  BookOpen,
  Map,
  ShieldCheck,
  Trophy,
  Newspaper,
  Briefcase,
  Building2,
  MessageSquare,
  ArrowRight,
  Menu as MenuIcon,
  X as CloseIcon,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

const PRODUCT_MENU = [
  { label: "Escáner gratuito", to: "/", icon: Search, desc: "Analiza tu sitio en 30 segundos · sin registro" },
  { label: "Generador de documentos", to: "/modulo-pro", icon: FileText, badge: "7 docs", desc: "Política, RAT, DPA, CCM y 3 más" },
  { label: "Deep Scan multi-página", to: "/cuenta#deep-scan", icon: Search, badge: "Pro", desc: "Analiza hasta 5 páginas internas" },
  { label: "Simulador de multas", to: "/simulador-multas", icon: Calculator, desc: "Estima tu rango de sanción APDP" },
  { label: "Catálogo de Datos (RAT)", to: "/modulo-pro", icon: ClipboardList, desc: "Documento que más solicita la APDP" },
];

const RESOURCES_MENU = [
  { label: "Blog", to: "/blog", icon: Newspaper, desc: "10+ artículos sobre Ley 21.719" },
  { label: "Casos · Antes/Después", to: "/casos", icon: Briefcase, badge: "Nuevo", desc: "5 sectores: e-commerce, clínica, ONG y más" },
  { label: "Guías por plataforma", to: "/guias", icon: BookOpen, desc: "WordPress, Shopify y más" },
  { label: "Checklist Art. 14 ter", to: "/cumplimiento-art-14-ter", icon: Map, badge: "12 ítems", desc: "Los 12 ítems obligatorios" },
  { label: "Transparencia", to: "/transparencia", icon: Trophy, desc: "Ranking público de sitios analizados" },
  { label: "Sobre nosotros", to: "/sobre-nosotros", icon: Building2, desc: "Quiénes somos · stack técnico" },
  { label: "Contacto", to: "/contacto", icon: MessageSquare, desc: "Soporte · demos · partnerships" },
];

function Dropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-3 w-[420px] bg-white border border-slate-200 rounded-2xl shadow-xl p-2 z-50">
          <div className="grid grid-cols-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.to + it.label}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center group-hover:bg-blue-100 transition-colors">
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{it.label}</p>
                      {it.badge && (
                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                          {it.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">{it.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const click = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <header className="border-b border-slate-200/70 bg-white/85 backdrop-blur-md sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="shrink-0">
          <span className="inline-flex items-center gap-2.5">
            <svg width="30" height="30" viewBox="0 0 64 64" aria-hidden="true">
              <defs>
                <linearGradient id="ldcGradH" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#1E40AF" />
                  <stop offset="100%" stopColor="#1E3A8A" />
                </linearGradient>
              </defs>
              <path d="M32 4 L56 12 V32 C56 48 32 60 32 60 C32 60 8 48 8 32 V12 Z" fill="url(#ldcGradH)" />
              <rect x="20" y="24" width="24" height="3.5" rx="1.2" fill="#FFFFFF" />
              <rect x="20" y="31" width="18" height="3.5" rx="1.2" fill="#FFFFFF" opacity="0.85" />
              <rect x="20" y="38" width="14" height="3.5" rx="1.2" fill="#FFFFFF" opacity="0.7" />
            </svg>
            <span className="leading-none">
              <span className="font-display tracking-tight text-slate-900 text-[18px] block">Ley de Datos</span>
              <span className="font-mono tracking-[0.22em] text-[9px] text-blue-700 uppercase block mt-0.5">Chile · 21.719</span>
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavLink data-testid="nav-escaner" to="/" end className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-blue-700" : "text-slate-700 hover:text-slate-900"}`}>
            Escáner
          </NavLink>
          <Dropdown testId="nav-producto" label="Producto" items={PRODUCT_MENU} />
          <Dropdown testId="nav-recursos" label="Recursos" items={RESOURCES_MENU} />
          <NavLink data-testid="nav-precios" to="/modulo-pro" className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-blue-700" : "text-slate-700 hover:text-slate-900"}`}>
            Precios
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                data-testid="user-menu"
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium"
              >
                <span className="w-6 h-6 rounded-full bg-blue-700 text-white grid place-items-center text-xs font-bold">
                  {user.email[0].toUpperCase()}
                </span>
                <span className="hidden sm:inline max-w-[120px] truncate">{user.email}</span>
                {user.es_pro && (
                  <span className="ml-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Pro</span>
                )}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                  <Link
                    to="/cuenta"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <UserIcon className="w-4 h-4" />
                    Mi cuenta
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                data-testid="nav-login"
                to="/login"
                className="hidden sm:inline-flex h-9 px-3 text-sm font-medium text-slate-700 hover:text-slate-900 items-center"
              >
                Iniciar sesión
              </Link>
              <Link
                data-testid="nav-cta"
                to="/"
                className="inline-flex h-9 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold items-center gap-1.5 transition-colors shadow-sm"
              >
                Escanea gratis
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
          <button
            className="lg:hidden inline-flex w-9 h-9 items-center justify-center rounded-lg border border-slate-200 ml-1"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-toggle"
            aria-label="Menú"
          >
            {mobileOpen ? <CloseIcon className="w-4 h-4" /> : <MenuIcon className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-slate-800">Escáner</Link>
            <p className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Producto</p>
            {PRODUCT_MENU.map((it) => (
              <Link key={it.to + it.label} to={it.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-slate-700 hover:text-blue-700">
                {it.label}{it.badge && <span className="ml-2 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{it.badge}</span>}
              </Link>
            ))}
            <p className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Recursos</p>
            {RESOURCES_MENU.map((it) => (
              <Link key={it.to + it.label} to={it.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm text-slate-700 hover:text-blue-700">
                {it.label}
              </Link>
            ))}
            <Link to="/modulo-pro" onClick={() => setMobileOpen(false)} className="block pt-3 text-sm font-medium text-slate-800">Precios</Link>
          </div>
        </div>
      )}
    </header>
  );
}
